import { useCallback, useEffect, useState } from "react";

import { api, ApiError, type QueryValue } from "./client";

export type ResourceState<T> = {
  readonly data: T | undefined;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
};

type InternalState<T> = {
  readonly data: T | undefined;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
};

// Fetch a GET resource, tracking loading/error/data. Pass `path === null` to
// skip the fetch (e.g. while a required route param is missing). `query` is
// keyed by value, not identity, so an inline object literal is safe.
export function useResource<T>(
  path: string | null,
  query?: Readonly<Record<string, QueryValue>>,
): ResourceState<T> {
  const [state, setState] = useState<InternalState<T>>({
    data: undefined,
    error: undefined,
    loading: path !== null,
  });
  const [nonce, setNonce] = useState(0);
  const refetch = useCallback((): void => setNonce((n) => n + 1), []);
  const queryKey = query ? JSON.stringify(query) : "";

  useEffect(() => {
    if (path === null) {
      setState({ data: undefined, error: undefined, loading: false });
      return;
    }
    const controller = new AbortController();
    setState((prev) => ({ ...prev, error: undefined, loading: true }));
    api
      .get<T>(path, { query, signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) {
          setState({ data, error: undefined, loading: false });
        }
      })
      .catch((err: unknown) => {
        // An aborted request (unmount / key change) is not a real error.
        if (controller.signal.aborted) {
          return;
        }
        const error =
          err instanceof ApiError
            ? err
            : new ApiError("unknown", "Unexpected error.", 0);
        setState({ data: undefined, error, loading: false });
      });
    return () => controller.abort();
    // `query` is captured via its stable serialisation `queryKey`.
  }, [path, queryKey, nonce]);

  return { ...state, refetch };
}
